// SDK ⇄ app boundary for the cross-session "Évaluations" results page (EvaluationsApi).
//
// The backend/SDK is **Evaluation**-named; the app page speaks the **survey**/`IGlobalSurveyRow`
// vocabulary and reuses the session-scoped `SessionSurveyResponsePanel` for the drill-down. Every alias
// between the two lives here so the store/components never touch an `Evaluation*` symbol directly.
//
// Known deltas handled here (SDK v1 — see backend handoff):
// - `globalNote` is a STRING on the wire → parsed to a number for the /10 display.
// - `EvaluationListMeta.responseRate` is treated as a 0–1 ratio (same as the session-tracking KPI) and
//   scaled to a 0–100 %. FLAG: re-verify the scale against live data (backend could already send 0–100).
// - The drill-down `answers[]` carry each question's title + type but NOT its option catalogue. That's why
//   a multiple-choice answer is rendered from `selectedOptionLabels` (the server's label snapshot, sdk
//   9.15.0) rather than resolved against the template. Non-respondents come back with no answers → the
//   panel lists no questions.
import { ExportEvaluationsDtoFormatEnum, GetEvaluationsTypeEnum } from "@simbelapp/order-sdk";
import type {
  EvaluationAnswer,
  EvaluationDetailResponse,
  EvaluationListMeta,
  EvaluationRow,
} from "@simbelapp/order-sdk";
import { SurveyActivityTypeEnum, SurveyTypeEnum } from "~/utils/enums/satisfaction-surveys.enums";
import {
  SessionSurveyParticipantStatusEnum,
  SessionSurveyRecipientEnum,
  SessionSurveyStatusEnum,
} from "~/utils/enums/session-surveys.enums";
import { EvaluationsExportFormatEnum, GlobalEvaluationsTabEnum } from "~/utils/enums/global-evaluations.enums";
import { QUESTION_TYPE_FROM_SDK, SURVEY_TYPE_FROM_SDK } from "~/utils/helpers/form-survey.mappers";
import type { ISurveyActivity } from "~/utils/interfaces/satisfaction-surveys-interfaces";
import type {
  IEvaluationDetail,
  IGlobalEvaluationsKpi,
  IGlobalSurveyRow,
} from "~/utils/interfaces/global-evaluations-interfaces";
import type {
  ISessionSurvey,
  ISessionSurveyAnswer,
  ISessionSurveyParticipant,
} from "~/utils/interfaces/session-surveys-interfaces";

// --- tab → SDK type (à chaud/à froid) ----------------------------------------
export const TAB_TO_SDK_TYPE: Record<GlobalEvaluationsTabEnum, GetEvaluationsTypeEnum> = {
  [GlobalEvaluationsTabEnum.HOT]: GetEvaluationsTypeEnum.Direct,
  [GlobalEvaluationsTabEnum.COLD]: GetEvaluationsTypeEnum.Delayed,
};

// --- export format → SDK format (enum member → the SDK's string-literal union) -
export const FORMAT_TO_SDK: Record<EvaluationsExportFormatEnum, ExportEvaluationsDtoFormatEnum> = {
  [EvaluationsExportFormatEnum.CSV]: ExportEvaluationsDtoFormatEnum.Csv,
  [EvaluationsExportFormatEnum.EXCEL]: ExportEvaluationsDtoFormatEnum.Excel,
};

// --- shared status/score helpers ---------------------------------------------
const STATUS_FROM_SDK: Record<string, SessionSurveyParticipantStatusEnum> = {
  scheduled: SessionSurveyParticipantStatusEnum.SCHEDULED,
  sent: SessionSurveyParticipantStatusEnum.SENT,
  completed: SessionSurveyParticipantStatusEnum.COMPLETED,
};

// `trainingTags` is typed `Array<object>` in the SDK, and the platform serves this shape inconsistently:
// the training-plan rows expose `tag_name` while the company-catalog rows expose `tagName`. Read both so
// the column works whichever the evaluations endpoint settled on, and drop anything unnamed.
function mapTrainingTagNames(tags?: Array<object> | null): string[] {
  return (tags ?? [])
    .map((tag) => {
      const t = tag as { tagName?: string | null; tag_name?: string | null };
      return t.tagName ?? t.tag_name ?? null;
    })
    .filter((name): name is string => !!name);
}

// --- list row + meta ---------------------------------------------------------
/** One cross-session evaluation row → the /evaluations table projection. */
export function mapEvaluationRow(row: EvaluationRow): IGlobalSurveyRow {
  return {
    id: row.recipientId, // track-by
    recipientId: row.recipientId,
    attendeeName: row.attendeeName,
    trainingName: row.trainingName ?? "—",
    prestataire: row.providerName ?? "—",
    startDate: row.sessionStartDate ?? null,
    endDate: row.sessionEndDate ?? null,
    status: STATUS_FROM_SDK[row.status] ?? SessionSurveyParticipantStatusEnum.SENT,
    bookingStatus: row.bookingStatus ?? "",
    globalScore: row.globalNote ?? null,
    sentAt: row.lastSentAt ?? null,
    respondedAt: row.responseDate ?? null,
    formName: row.formName ?? "—",
    managerName: row.managerName ?? "—",
    format: row.format ?? null,
    sessionItemType: row.sessionItemType ?? null,
    trainingType: row.trainingType ?? null,
    location: row.location ?? "—",
    sessionTag: row.sessionTag ?? "—",
    trainingTags: mapTrainingTagNames(row.trainingTags),
  };
}

/** List meta → the KPI chips + hot/cold tab counts (all server-computed). */
export function mapEvaluationMeta(meta: EvaluationListMeta): {
  kpi: IGlobalEvaluationsKpi;
  hotCount: number;
  coldCount: number;
} {
  return {
    kpi: {
      avgScore: meta.avgScore,
      responseRate: meta.responseRate == null ? null : Math.round(meta.responseRate * 100),
      pending: meta.pending,
      responded: meta.responded,
    },
    hotCount: meta.hotCount,
    coldCount: meta.coldCount,
  };
}

// --- drill-down detail → the reused SessionSurveyResponsePanel shape ----------
function mapAnswerToQuestion(answer: EvaluationAnswer, index: number): ISurveyActivity {
  return {
    id: answer.questionId,
    type: QUESTION_TYPE_FROM_SDK[answer.type] ?? null,
    order: index,
    title: answer.title,
  };
}

function mapAnswer(answer: EvaluationAnswer): ISessionSurveyAnswer {
  const type = QUESTION_TYPE_FROM_SDK[answer.type] ?? SurveyActivityTypeEnum.OPEN;
  // `passedRate` is only set for graded ("notée") questions; null everywhere else.
  const passedRate = answer.passedRate ?? null;
  if (type === SurveyActivityTypeEnum.MULTIPLE_CHOICE || type === SurveyActivityTypeEnum.MULTIPLE_CHOICE_SCORED) {
    // Prefer the server's label snapshot (sdk 9.15.0): the panel needs human-readable answers, and a
    // snapshot is also more faithful for a historical response — it shows what the learner actually saw,
    // even if the option has since been relabelled. Fall back to the ids so nothing renders blank.
    return {
      questionId: answer.questionId,
      type,
      value: answer.selectedOptionLabels ?? answer.selectedOptions ?? [],
      passedRate,
    };
  }
  if (type === SurveyActivityTypeEnum.ASSOCIATION) {
    // Keyed by the prompt TEXT rather than leftId: the panel shows the pairing to a human, and the ids
    // mean nothing there. `chosen` is null when the learner skipped that prompt.
    return {
      questionId: answer.questionId,
      type,
      value: Object.fromEntries((answer.pairs ?? []).map((pair) => [pair.prompt, pair.chosen ?? "—"])),
      passedRate,
    };
  }
  if (type === SurveyActivityTypeEnum.GAP_FILLING) {
    return {
      questionId: answer.questionId,
      type,
      // "Réponse", not "Trou": these rows show what the learner ANSWERED, so the label names the answer
      // rather than the blank it went into. Keeps the marker number so each row still maps to the [0]/[1]
      // visible in the question title above (the builder side keeps "Trou [n]", where you ARE defining
      // the blank).
      value: Object.fromEntries((answer.gapAnswers ?? []).map((gap) => [`Réponse [${gap.order}]`, gap.value ?? "—"])),
      passedRate,
    };
  }
  if (type === SurveyActivityTypeEnum.RATING) {
    return { questionId: answer.questionId, type, value: answer.value != null ? Number(answer.value) : "", passedRate };
  }
  return { questionId: answer.questionId, type, value: answer.value ?? "", passedRate };
}

/** One evaluation detail → the {survey, participant, context} the response side-panel renders. */
export function mapEvaluationDetail(detail: EvaluationDetailResponse): IEvaluationDetail {
  const type = SURVEY_TYPE_FROM_SDK[detail.type] ?? SurveyTypeEnum.DIRECT;
  const survey: ISessionSurvey = {
    id: detail.recipientId, // the panel doesn't use a real survey id here
    type,
    templateId: "",
    templateName: detail.formName,
    status: SessionSurveyStatusEnum.SENT,
    sentAt: detail.lastSentAt ?? null,
    schedule: null,
    recipients:
      type === SurveyTypeEnum.DIRECT ? SessionSurveyRecipientEnum.PARTICIPANTS : SessionSurveyRecipientEnum.MANAGERS,
    questions: detail.answers.map(mapAnswerToQuestion),
    participants: [],
  };
  const participant: ISessionSurveyParticipant = {
    id: detail.recipientId,
    token: detail.recipientId, // the recipient id IS the personalized-link token
    name: detail.attendeeName,
    attendeeName: detail.attendeeName,
    managerName: detail.managerName ?? null,
    status: STATUS_FROM_SDK[detail.status] ?? SessionSurveyParticipantStatusEnum.SENT,
    sentAt: detail.lastSentAt ?? null,
    respondedAt: detail.responseDate ?? null,
    globalScore: detail.globalNote ?? null,
    answers: detail.answers.map(mapAnswer),
  };
  return {
    survey,
    participant,
    formationName: detail.trainingName ?? "",
    providerName: detail.providerName ?? "",
    sessionId: detail.sessionId,
  };
}
